/**
 * Next Step CTA Section
 *
 * Milestone-style call-to-action at the bottom of the page
 * encouraging users to move to the next phase of their journey.
 * Opens a confirmation modal before changing the journey stage.
 */

import { useState } from "react";
import {
  View,
  Pressable,
  Modal as RNModal,
  Platform,
  Image,
} from "react-native";
import { Text } from "@louise/ui/Text/Text";
import { Modal } from "@louise/ui/Modal/Modal";
import AnimatedRN, { FadeInDown } from "react-native-reanimated";
import {
  ArrowRight,
  MoonStar,
  Sparkles,
  HeartHandshake,
  Clock,
  X,
  ChevronRight,
  ClipboardList,
} from "lucide-react-native";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// Inject CSS animation for border beam effect (web only)
if (Platform.OS === "web" && typeof document !== "undefined") {
  const styleId = "next-step-beam-animation";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes next-step-beam {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .next-step-beam-gradient {
        animation: next-step-beam 4s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }
}

// =============================================================================
// ConfirmationModal - Reusable confirmation modal
// =============================================================================

export type ConfirmationModalProps = {
  /** Whether the modal is visible */
  visible: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when user confirms */
  onConfirm: () => void;
  /** Modal title */
  modalTitle: string;
  /** Modal description */
  modalDescription: string;
  /** Confirm button text */
  modalConfirmText?: string;
  /** Cancel button text */
  modalCancelText?: string;
  /** Helper text shown below buttons */
  modalHelperText?: string;
};

export function ConfirmationModal({
  visible,
  onClose,
  onConfirm,
  modalTitle,
  modalDescription,
  modalConfirmText = "Yes, continue",
  modalCancelText = "Not yet",
  modalHelperText,
}: ConfirmationModalProps) {
  const capturePress = usePressCapture("ConfirmationModal");
  const handleConfirm = () => {
    capturePress(pressLabel(modalConfirmText));
    onClose();
    onConfirm();
  };
  const handleClose = () => {
    capturePress(pressLabel(modalCancelText));
    onClose();
  };

  return (
    <Modal
      open={visible}
      onClose={onClose}
      size={320}
      backgroundStyle={{ backgroundColor: "#0F172A" }}
    >
      <View className="items-center px-6 pb-6 pt-8">
        {/* Icon */}
        <View
          className="mb-5 h-14 w-14 items-center justify-center rounded-full"
          style={{
            backgroundColor: "rgba(74, 222, 128, 0.1)",
            borderWidth: 1,
            borderColor: "rgba(74, 222, 128, 0.2)",
          }}
        >
          <ArrowRight size={24} color="#4ADE80" strokeWidth={2} />
        </View>

        {/* Title */}
        <Text className="mb-3 text-center text-xl font-semibold tracking-tight text-white">
          {modalTitle}
        </Text>

        {/* Description */}
        <Text className="mb-6 text-center text-sm leading-relaxed text-slate-400">
          {modalDescription}
        </Text>

        {/* Confirm Button */}
        <Pressable
          onPress={handleConfirm}
          className="mb-3 w-full items-center rounded-xl py-3.5"
          style={{
            backgroundColor: "#4ADE80",
            shadowColor: "#4ADE80",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.3,
            shadowRadius: 12,
          }}
        >
          <Text className="text-[0.9rem] font-semibold text-[#020617]">
            {modalConfirmText}
          </Text>
        </Pressable>

        {/* Cancel Button */}
        <Pressable
          onPress={handleClose}
          className="w-full items-center rounded-xl py-3.5"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            borderWidth: 1,
            borderColor: "rgba(255, 255, 255, 0.1)",
          }}
        >
          <Text className="text-[0.9rem] font-medium text-slate-300">
            {modalCancelText}
          </Text>
        </Pressable>

        {/* Helper Text */}
        {modalHelperText && (
          <Text className="mt-4 text-center text-xs text-slate-500">
            {modalHelperText}
          </Text>
        )}
      </View>
    </Modal>
  );
}

// =============================================================================
// OutcomeModal - Compassionate outcome selection
// =============================================================================

function OutcomeModal({
  visible,
  onClose,
  onPregnant,
  onNotPregnant,
}: {
  visible: boolean;
  onClose: () => void;
  onPregnant: () => void;
  onNotPregnant: () => void;
}) {
  const capturePress = usePressCapture("OutcomeModal");
  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View
        className="flex-1 items-center justify-center px-4"
        style={{ backgroundColor: "rgba(2, 6, 23, 0.9)" }}
      >
        {/* Modal Card */}
        <View
          className="relative w-full max-w-[22rem] overflow-hidden rounded-3xl border border-white/10 p-6"
          style={{ backgroundColor: "#0B1121" }}
        >
          {/* Background Ambience */}
          <View
            className="absolute -right-12 -top-12 h-48 w-48 rounded-full"
            style={{ backgroundColor: "rgba(196, 181, 253, 0.1)" }}
            pointerEvents="none"
          />
          <View
            className="absolute -bottom-5 -left-5 h-40 w-40 rounded-full"
            style={{ backgroundColor: "rgba(74, 222, 128, 0.05)" }}
            pointerEvents="none"
          />

          {/* Content */}
          <View className="relative z-10 w-full items-center">
            {/* Gentle Icon */}
            <View
              className="mb-5 h-12 w-12 items-center justify-center rounded-full border border-white/5 bg-white/5"
              style={{
                shadowColor: "#C4B5FD",
                shadowOffset: { width: 0, height: 0 },
                shadowOpacity: 0.3,
                shadowRadius: 15,
              }}
            >
              <MoonStar size={22} color="#C4B5FD" strokeWidth={1.5} />
            </View>

            <Text
              className="mb-3 text-center text-xl font-medium tracking-tight text-white"
              style={{ fontFamily: "Tobias-Thin" }}
            >
              Closing this chapter
            </Text>
            <Text className="mb-8 px-2 text-center text-[0.85rem] font-light leading-relaxed text-slate-400">
              We&apos;re here to support you, whatever the outcome. How would
              you like to proceed with your journey?
            </Text>

            <View className="w-full gap-3">
              {/* Option 1: Pregnant */}
              <Pressable
                onPress={() => {
                  capturePress("I'm pregnant");
                  onPregnant();
                }}
                className="w-full flex-row items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 active:scale-[0.98] active:border-[#4ADE80]/30 active:bg-[#4ADE80]/5"
              >
                <View className="h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#4ADE80]/20 bg-[#4ADE80]/10">
                  <Sparkles size={18} color="#4ADE80" />
                </View>
                <View className="flex-1">
                  <Text className="text-[0.9rem] font-medium text-slate-200">
                    I&apos;m pregnant
                  </Text>
                  <Text className="text-[0.7rem] font-light text-slate-500">
                    Start pregnancy tracking
                  </Text>
                </View>
                <ChevronRight size={16} color="#475569" />
              </Pressable>

              {/* Option 2: Not Pregnant */}
              <Pressable
                onPress={() => {
                  capturePress("This cycle didn't work out");
                  onNotPregnant();
                }}
                className="w-full flex-row items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4 active:scale-[0.98] active:border-[#C4B5FD]/30 active:bg-[#C4B5FD]/5"
              >
                <View className="h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#C4B5FD]/20 bg-[#C4B5FD]/10">
                  <HeartHandshake size={18} color="#C4B5FD" />
                </View>
                <View className="flex-1">
                  <Text className="text-[0.9rem] font-medium text-slate-200">
                    This cycle didn&apos;t work out
                  </Text>
                  <Text className="text-[0.7rem] font-light text-slate-500">
                    Explore support options
                  </Text>
                </View>
                <ChevronRight size={16} color="#475569" />
              </Pressable>

              {/* Option 3: Postpone */}
              <Pressable
                onPress={() => {
                  capturePress("I'm not ready to share yet");
                  onClose();
                }}
                className="mt-2 w-full flex-row items-center justify-center gap-2 py-3"
              >
                <Clock size={14} color="#64748b" />
                <Text className="text-[0.8rem] font-medium text-slate-500">
                  I&apos;m not ready to share yet
                </Text>
              </Pressable>
            </View>
          </View>

          {/* Close Button */}
          <Pressable
            onPress={() => {
              capturePress("outcome_modal_close");
              onClose();
            }}
            className="absolute right-4 top-4 z-20 rounded-full p-2 active:bg-white/5"
          >
            <X size={18} color="#475569" />
          </Pressable>
        </View>
      </View>
    </RNModal>
  );
}

// =============================================================================
// NextStepCTA
// =============================================================================

export type NextStepCTAProps = {
  /** Title shown above the button */
  title?: string;
  /** Subtitle shown below title */
  subtitle?: string;
  /** Button text */
  buttonText?: string;
  /** Modal title (e.g., "Begin Preparation?") */
  modalTitle?: string;
  /** Modal description */
  modalDescription?: string;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Callback when user confirms the stage change */
  onConfirm?: () => void;
  /** @deprecated Use onConfirm instead */
  onPress?: () => void;
  /** Animation delay */
  delay?: number;
  /** Show outcome modal instead of confirmation modal */
  showOutcomeModal?: boolean;
  /** Callback when user selects "I'm pregnant" */
  onPregnant?: () => void;
  /** Callback when user selects "This cycle didn't work out" */
  onNotPregnant?: () => void;
  /** Skip modal and call onConfirm directly when button is pressed */
  skipModal?: boolean;
  /** Visual variant - "default" (amber) or "complete" (purple) */
  variant?: "default" | "complete";
};

export function NextStepCTA({
  title = "Ready for the next chapter?",
  subtitle,
  buttonText = "Continue to preparation",
  modalTitle = "Begin Preparation?",
  modalDescription = "Moving forward will unlock your custom checklist and update your timeline.",
  confirmText = "Yes, update my status",
  cancelText = "Maybe later",
  onConfirm,
  onPress,
  delay = 400,
  showOutcomeModal = false,
  onPregnant,
  onNotPregnant,
  skipModal = false,
  variant = "default",
}: NextStepCTAProps) {
  // Color scheme based on variant
  const isPurple = variant === "complete";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const capturePress = usePressCapture("NextStepCTA");

  const handleConfirm = async () => {
    capturePress(pressLabel(confirmText));
    console.log(
      "[NextStepCTA] handleConfirm called, onConfirm:",
      !!onConfirm,
      "onPress:",
      !!onPress,
    );
    try {
      // Await callback before closing modal to prevent race conditions
      await (onConfirm?.() ?? onPress?.());
    } catch (error) {
      console.error("[NextStepCTA] handleConfirm error:", error);
    }
    setIsModalOpen(false);
  };

  const handlePregnant = async () => {
    try {
      await onPregnant?.();
    } catch (error) {
      console.error("[NextStepCTA] handlePregnant error:", error);
    }
    setIsModalOpen(false);
  };

  const handleNotPregnant = async () => {
    try {
      await onNotPregnant?.();
    } catch (error) {
      console.error("[NextStepCTA] handleNotPregnant error:", error);
    }
    setIsModalOpen(false);
  };

  const handleButtonPress = async () => {
    capturePress(pressLabel(buttonText));
    console.log(
      "[NextStepCTA] Button pressed, skipModal:",
      skipModal,
      "showOutcomeModal:",
      showOutcomeModal,
    );
    if (skipModal) {
      // Skip modal and call onConfirm directly
      await (onConfirm?.() ?? onPress?.());
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <AnimatedRN.View
        entering={FadeInDown.delay(delay)}
        style={{ marginBottom: 48 }}
      >
        {/* Milestone Card with Animated Border */}
        <View
          style={{
            position: "relative",
            width: "100%",
            marginTop: 40,
          }}
        >
          {/* Floating 3D Image - outside overflow container */}
          <View
            style={{
              position: "absolute",
              top: -36,
              left: 0,
              right: 0,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <Image
              source={require("@assets/images/3d/medal.png")}
              style={{
                width: 80,
                height: 80,
              }}
              resizeMode="contain"
            />
          </View>

          {/* Card with border beam */}
          <View
            style={{
              position: "relative",
              borderRadius: 20,
              overflow: "hidden",
              width: "100%",
            }}
          >
            {/* Rotating border beam */}
            <View
              // @ts-ignore - web className
              className="next-step-beam-gradient"
              style={{
                position: "absolute",
                top: -80,
                left: -80,
                right: -80,
                bottom: -80,
                // @ts-ignore - web only
                background: isPurple
                  ? "conic-gradient(from 0deg, transparent 0deg, rgba(196, 181, 253, 0.5) 30deg, transparent 60deg)"
                  : "conic-gradient(from 0deg, transparent 0deg, rgba(245, 158, 11, 0.5) 30deg, rgba(245, 158, 11, 0.2) 50deg, transparent 80deg)",
                zIndex: 0,
              }}
            />

            {/* Inner card content */}
            <Pressable
              onPress={handleButtonPress}
              style={{
                margin: 1,
                borderRadius: 19,
                backgroundColor: "#0B1121",
                paddingTop: 52,
                paddingBottom: 28,
                paddingHorizontal: 24,
                alignItems: "center",
              }}
            >
              {/* Title */}
              <Text
                style={{
                  fontSize: 18,
                  color: "#ffffff",
                  fontFamily: "Tobias-Thin",
                  textAlign: "center",
                  marginBottom: 10,
                }}
              >
                {title}
              </Text>

              {/* Context text */}
              <Text
                style={{
                  fontSize: 13,
                  color: "#94a3b8",
                  textAlign: "center",
                  marginBottom: 20,
                  lineHeight: 20,
                  paddingHorizontal: 8,
                }}
              >
                {subtitle ||
                  "Now that you've explored your options, let's get you ready for your next step, which is your first consult."}
              </Text>

              {/* CTA Button */}
              <Pressable onPress={handleButtonPress}>
                {({ pressed, hovered }) => (
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 10,
                      paddingVertical: 10,
                      paddingHorizontal: 16,
                      borderRadius: 8,
                      backgroundColor:
                        hovered || pressed
                          ? "rgba(245, 158, 11, 0.15)"
                          : "rgba(255, 255, 255, 0.05)",
                      borderWidth: 1,
                      borderColor:
                        hovered || pressed
                          ? "rgba(245, 158, 11, 0.4)"
                          : "rgba(255, 255, 255, 0.15)",
                      // @ts-ignore - web only
                      boxShadow:
                        hovered || pressed
                          ? "0 0 20px rgba(245, 158, 11, 0.3)"
                          : "none",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <ClipboardList
                      size={16}
                      color={hovered || pressed ? "#F59E0B" : "#ffffff"}
                      strokeWidth={2}
                    />
                    <Text
                      style={{
                        fontSize: 13,
                        fontWeight: "600",
                        color: hovered || pressed ? "#F59E0B" : "#ffffff",
                      }}
                    >
                      {buttonText}
                    </Text>
                    <ArrowRight
                      size={14}
                      color={hovered || pressed ? "#F59E0B" : "#ffffff"}
                      strokeWidth={2}
                    />
                  </View>
                )}
              </Pressable>
            </Pressable>
          </View>
        </View>
      </AnimatedRN.View>

      {/* Modal - Outcome or Confirmation */}
      {showOutcomeModal ? (
        <OutcomeModal
          visible={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onPregnant={handlePregnant}
          onNotPregnant={handleNotPregnant}
        />
      ) : (
        <Modal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          size={320}
          backgroundStyle={{ backgroundColor: "#0F172A" }}
        >
          <View className="items-center px-6 pb-6 pt-8">
            {/* Icon */}
            <View
              className="mb-5 h-14 w-14 items-center justify-center rounded-full"
              style={{
                backgroundColor: "rgba(74, 222, 128, 0.1)",
                borderWidth: 1,
                borderColor: "rgba(74, 222, 128, 0.2)",
              }}
            >
              <ArrowRight size={24} color="#4ADE80" strokeWidth={2} />
            </View>

            {/* Title */}
            <Text className="mb-3 text-center text-xl font-semibold tracking-tight text-white">
              {modalTitle}
            </Text>

            {/* Description */}
            <Text className="mb-6 text-center text-sm leading-relaxed text-slate-400">
              {modalDescription}
            </Text>

            {/* Confirm Button */}
            <Pressable
              onPress={handleConfirm}
              className="mb-3 w-full items-center rounded-xl py-3.5"
              style={{
                backgroundColor: "#4ADE80",
                shadowColor: "#4ADE80",
                shadowOffset: { width: 0, height: 0 },
                shadowOpacity: 0.3,
                shadowRadius: 12,
              }}
            >
              <Text className="text-[0.9rem] font-semibold text-[#020617]">
                {confirmText}
              </Text>
            </Pressable>

            {/* Cancel Button */}
            <Pressable
              onPress={() => {
                capturePress(pressLabel(cancelText));
                setIsModalOpen(false);
              }}
              className="w-full items-center rounded-xl py-3.5"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderWidth: 1,
                borderColor: "rgba(255, 255, 255, 0.1)",
              }}
            >
              <Text className="text-[0.9rem] font-medium text-slate-300">
                {cancelText}
              </Text>
            </Pressable>
          </View>
        </Modal>
      )}
    </>
  );
}
